import type { ISiteOption } from "@/customTypings/customer/Customer";
import type { IWasteMovementDetail } from "@/customTypings/wasteMovement/WasteMovement";
import { isSiteScopedFormReadOnly } from "@/components/Layout/ParentEntityLifecycleBanner";
import { filterReportingEligibleSites } from "./wasteMovementSiteSelection";

const MUTATING_ROLES = new Set([
    "System Administrator",
    "Administrator",
    "Office Staff",
    "Yard Staff",
]);

export type WasteMovementBlockReason =
    | "none"
    | "role"
    | "siteDisabled"
    | "siteDeleted"
    | "siteNotReporting"
    | "noReportingSites"
    | "recordDeleted"
    | "siteReadOnly";

export type WasteMovementFormAccess = {
    canView: boolean;
    canMutate: boolean;
    readOnlyForm: boolean;
    blockReason: WasteMovementBlockReason;
    permissionMessage: string | null;
};

export function canMutateWasteMovements(roleNames: string[]): boolean {
    return roleNames.some((r) => MUTATING_ROLES.has(r));
}

function buildRolePermissionMessage(entityLabel: string): string {
    return `${entityLabel} records are read-only for your account. Yard Staff, Office Staff, and Administrators can create and edit movements.`;
}

function buildSiteNotReportingMessage(): string {
    return "This site is not active for reporting. Operational waste movements cannot be created or changed until reporting is re-enabled.";
}

export function resolveWasteMovementFormAccess(args: {
    isNew: boolean;
    roleNames: string[];
    entityLabel: string;
    detail?: IWasteMovementDetail | null;
    reportingEligibleSites?: ReadonlyArray<ISiteOption>;
}): WasteMovementFormAccess {
    const { isNew, roleNames, entityLabel, detail, reportingEligibleSites = [] } = args;
    const roleCanMutate = canMutateWasteMovements(roleNames);

    if (isNew) {
        if (!roleCanMutate) {
            return {
                canView: false,
                canMutate: false,
                readOnlyForm: true,
                blockReason: "role",
                permissionMessage: buildRolePermissionMessage(entityLabel),
            };
        }

        if (filterReportingEligibleSites(reportingEligibleSites).length === 0) {
            return {
                canView: false,
                canMutate: false,
                readOnlyForm: true,
                blockReason: "noReportingSites",
                permissionMessage: "No reporting-active sites are available.",
            };
        }

        return {
            canView: true,
            canMutate: true,
            readOnlyForm: false,
            blockReason: "none",
            permissionMessage: null,
        };
    }

    if (!detail) {
        return {
            canView: false,
            canMutate: false,
            readOnlyForm: true,
            blockReason: "none",
            permissionMessage: null,
        };
    }

    const siteAccessLevel = detail.siteAccessLevel ?? "readWrite";
    const canMutate =
        detail.canMutate ??
        (roleCanMutate &&
            siteAccessLevel === "readWrite" &&
            detail.siteReportingActiveFlag !== false &&
            !detail.siteIsDeleted &&
            detail.siteIsEnabled !== false &&
            !detail.isDeleted);

    const readOnlyForm = isSiteScopedFormReadOnly(detail.isDeleted, siteAccessLevel) || !canMutate;

    let blockReason: WasteMovementBlockReason = "none";
    let permissionMessage: string | null = null;

    if (!roleCanMutate) {
        blockReason = "role";
        permissionMessage = buildRolePermissionMessage(entityLabel);
    } else if (detail.isDeleted) {
        blockReason = "recordDeleted";
    } else if (detail.siteIsDeleted) {
        blockReason = "siteDeleted";
    } else if (detail.siteIsEnabled === false) {
        blockReason = "siteDisabled";
    } else if (detail.siteReportingActiveFlag === false) {
        blockReason = "siteNotReporting";
        permissionMessage = buildSiteNotReportingMessage();
    } else if (siteAccessLevel !== "readWrite") {
        blockReason = "siteReadOnly";
    }

    return {
        canView: true,
        canMutate,
        readOnlyForm,
        blockReason,
        permissionMessage,
    };
}

export function isRowOperationallyEligible(row: {
    siteIsEnabled: boolean;
    siteIsDeleted: boolean;
    siteReportingActiveFlag?: boolean;
}): boolean {
    return (
        row.siteIsEnabled &&
        !row.siteIsDeleted &&
        row.siteReportingActiveFlag !== false
    );
}
